//// Application Configuration

//// Application Packages
import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { object } from 'yup'
import { translateField } from '../../functions/field-functions'
import { extractSubmissionForm } from '../../functions/form-functions'

//// Application Slices
import { selectSettings } from '../../reducers/settingSlice'
import {
    updateFormValidity,
    updateFormSystem,
    selectForms,
} from '../../reducers/formSlice'

//// Application Components
import DividerComponent from '../../components/simple-components/divider-component'
import ButtonComponent from '../../components/conditional-components/button-component'
import LoadingComponent from '../../components/simple-components/loading-component'
import NotificationComponent from '../../components/conditional-components/notification-component'
import FileUploadComponent from '../../components/file-upload-components/file-upload-component'

//// Carbon Components
import { Form } from '@carbon/react'

//// Carbon Icons

//// Static Constants

//////// Component
const LicensedPracticalNursingExaminationFileAttachmentsForm = ({
    instance,
}) => {
    //////// Hooks
    const dispatch = useDispatch()
    const navigate = useNavigate()

    //////// State
    const [loading, setLoading] = useState(false)
    const [notification, setNotification] = useState({})

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const formsData = useSelector(selectForms)

    //// Slice Data
    const digital_name = settingsData?.['form']?.['digital_name']
    const submit_route = settingsData?.['form']?.['submit_route']
    const fields = settingsData?.['form']?.['fields']

    //////// Search Params

    //////// Methods

    // Validate Data Submission
    const validation = async (submit) => {
        // Validate Form Submission Data Against Schema
        try {
            // Schema Object
            const schema = {}
            schema.yup = object({})
            schema.valid = {}
            await schema.yup.validate(
                { ...submit },
                { strict: true, abortEarly: false }
            )
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: {},
                })
            )
            return true
        } catch (err) {
            const errors = {}
            if (err?.inner?.length > 0) {
                await err.inner.forEach(async (el) => {
                    if (el?.path) {
                        errors[el?.path] = true
                        if (el?.errors?.length > 0) {
                            const errName = el?.path + 'ErrMsg'
                            const errMsg = el?.errors?.join(' ')
                            const tran = translateField(fields, el?.path)
                            if (tran) {
                                const tranErrMsg = await errMsg.replaceAll(
                                    el?.path,
                                    tran
                                )
                                errors[errName] = tranErrMsg
                            } else {
                                errors[errName] = el?.errors?.join(' ')
                            }
                        }
                    }
                })
            }
            //// Start Watson Assistant Journey

            //// End Watson Assistant Journey
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: errors,
                })
            )
            return false
        }
    }

    //////// Functional Methods

    // Handle Form Submission
    const handleSubmit = async () => {
        try {
            console.info('File Attachments: Submitted')

            setLoading(true)

            // Extract Form Data From Redux
            const submit = await extractSubmissionForm(digital_name, formsData)

            // Validate Form Submission Data
            const valid = await validation(submit)

            // Check If Form Data Is Valid
            if (valid) {
                console.info('File Attachments: Passed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: true },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: false,
                    kind: '',
                    title: '',
                    subtitle: '',
                })

                // Navigate To Next Form
                navigate(submit_route ? submit_route : '/')
            } else {
                console.info('File Attachments: Failed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: false },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: true,
                    kind: 'error',
                    title: 'Form Errors:',
                    subtitle:
                        'There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.',
                })

                console.log(
                    'File Attachments Failed Valid: There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.'
                )
            }
        } catch (error) {
            console.warn('Handle Submit Error:', error)
            return null
        }
    }

    // Handle Keyboard Enter As Submit (Method)
    const handleKeyDown = async (event) => {
        try {
            if (event?.key === 'Enter') {
                await handleSubmit()
            }
        } catch (error) {
            console.warn('Handle Down Key Error:', error)
            return null
        }
    }

    //////// Memoization

    //////// Effects/Side Effects

    // Reload Watson Assistant Journey on Reload
    useEffect(() => {
        if (instance?.tours) {
            instance.tours.endTour()
            instance.tours.startTour(
                'Registered Nursing Endorsement File Attachment Tour'
            )
        }
        return () => {}
    }, [instance])

    //////////////// Render & Return
    if (loading) {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <LoadingComponent />
                </Form>
            </>
        )
    } else {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <p>
                        Attach your supporting documents using the Browse and
                        Attach features below.
                    </p>

                    <DividerComponent text="Social Security Number" />

                    <p>
                        <strong>Social Security Number -</strong> If you do not
                        currently have a U.S. Social Security number, you will
                        be required to submit your Social Security number to the
                        Board of Nursing prior to licensure.
                    </p>

                    <p>
                        If you answered &quot;Yes&quot; to any questions in the
                        sections below, you will be required to upload
                        additional documentation:
                    </p>

                    <DividerComponent text="Health History" />

                    <ul>
                        <li>
                            <strong>
                                A letter from a Licensed Health Care
                                Practitioner,
                            </strong>{' '}
                            who is qualified by skill and training to address
                            the condition identified, which explains the impact
                            the condition may have on the ability to practice
                            the profession with reasonable skill and safety. The
                            letter must specify that the applicant is safe to
                            practice the profession without restrictions or
                            specifically indicate the restrictions that are
                            necessary. Documentation provided must be dated
                            within one year of the application date.
                        </li>
                        <li>
                            <strong> A written self-explanation,</strong>{' '}
                            identifying the medical condition(s) or
                            occurrence(s), and current status.
                        </li>
                    </ul>

                    <DividerComponent text="Discipline History" />

                    <ul>
                        <li>
                            <strong>Self-Explanation</strong>, describing in
                            detail the circumstances surrounding the
                            disciplinary action.
                        </li>
                        <li>
                            A copy of the{' '}
                            <strong>Administrative Complaint</strong> and{' '}
                            <strong>Final Order.</strong>
                        </li>
                        <li>
                            Three current (written within the last year)
                            professional{' '}
                            <strong> Letters of Recommendation.</strong>
                        </li>
                    </ul>

                    <DividerComponent text="Criminal History" />

                    <ul>
                        <li>
                            <strong>Self-Explanation</strong> describing in
                            detail the circumstances surrounding each offense;
                            including dates, city and state, charges and final
                            results.
                        </li>
                        <li>
                            <strong>Final Dispositions</strong> and{' '}
                            <strong>Arrest Records</strong> or all offenses. The
                            Clerk of the Court in the arresting jurisdiction
                            will provide you with these documents.
                            Unavailability of these documents must come in the
                            form of a letter from the Clerk of the Court.
                        </li>
                        <li>
                            <strong>Completion of Sentence Documents.</strong>{' '}
                            You may obtain document from the Department of
                            Corrections. The report must include the start date,
                            end date and that the conditions were met.
                        </li>
                        <li>
                            Three current (written within the last year)
                            professional{' '}
                            <strong> Letters of Recommendation.</strong>
                        </li>
                    </ul>

                    <DividerComponent text="Medicaid / Medicare" />

                    <p>
                        <strong> Medicaid -</strong> Drug Court - Provide
                        supporting documentation of this program completion,
                        including court dispositions or agency orders.
                    </p>

                    <p>
                        <strong> Medicaid - Termination -</strong> If you have
                        been terminated for cause from the Florida Medicaid
                        Program, the Federal Medicare Program, or another
                        state&rsquo;s Medicaid Program, provide supporting
                        documentation, including any reinstatement information.
                    </p>

                    <DividerComponent text="Military Veteran" />

                    <p>
                        <strong>Military Veteran Spouse Fee Waiver -</strong> If
                        your application indicates that your spouse was
                        honorably discharged from a branch of the United States
                        Armed Forces within the previous 60 months, attach a
                        copy of the DD-214 or NGB-22 and a copy of your marriage
                        certificate.
                    </p>

                    <p>
                        <strong> Military Veteran Fee Waiver -</strong> If your
                        application indicates that you were honorably discharged
                        from a branch of the United States Armed Forces within
                        the previous 60 months, attach a copy of your DD-214 or
                        NGB-22. You will be able to attach documents after
                        submitting this application using the Application Status
                        Check, Upload Feature.{' '}
                    </p>
                    <p>
                        <strong>
                            Applicants with questions regarding Visas or work
                            permits should contact:
                        </strong>
                    </p>

                    <p className="fldoh-address-box">
                        Bureau of Immigration and Customs Enforcement
                        <br />
                        4255 &quot;I&quot; Street N.W.
                        <br />
                        Washington D.C. 20536, USA
                    </p>

                    <p>
                        Phone: (800) 375-5283
                        <br />
                        Web:{' '}
                        <a
                            href="https://www.uscis.gov/"
                            target="_blank"
                            rel="noreferrer noopener"
                            aria-label="cgfns-link"
                            type="link"
                        >
                            https://www.uscis.gov/
                        </a>
                    </p>

                    <p>
                        <strong>For Visa Screenings contact:</strong>
                    </p>

                    <p className="fldoh-address-box">
                        Commission on Graduates of Foreign Nursing Schools
                        (CGFNS)
                        <br />
                        3600 Market Street
                        <br />
                        Philadelphia, PA 19104, USA
                    </p>

                    <p>
                        Phone: (215) 349-8767
                        <br />
                        Web:{' '}
                        <a
                            href="https://www.cgfns.org"
                            target="_blank"
                            rel="noreferrer noopener"
                            aria-label="cgfns-link"
                            type="link"
                        >
                            https://www.cgfns.org
                        </a>
                    </p>
                </Form>

                <div className="file-upload-group">
                    <div className="file-upload-group-item">
                        <FileUploadComponent />
                    </div>
                </div>

                <NotificationComponent notification={notification} />

                <div className="button-group">
                    <div className="button-group-item">
                        <ButtonComponent
                            click={handleSubmit}
                            title="Save Progress"
                        />
                    </div>
                </div>
            </>
        )
    }
}

export default LicensedPracticalNursingExaminationFileAttachmentsForm
